import { cn } from '@/utils/utils';
import { Node, isNodeEmpty } from '@tiptap/core';
import Document from '@tiptap/extension-document';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { TextSelection } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import {
  CustomKeymap,
  // CodeBlockLowlight,
  HighlightExtension,
  // HorizontalRule,
  // MarkdownExtension,
  Placeholder,
  StarterKit,
  TaskItem,
  TaskList,
  TextStyle,
  TiptapImage,
  TiptapLink,
  TiptapUnderline,
} from 'novel';
import { UploadImagesPlugin } from 'novel';
import { Markdown } from 'tiptap-markdown';
// import { common, createLowlight } from 'lowlight';
import CustomSearch from '../Plugins/custom-search';
// import { AutocompleteExtension } from './autocomplete-extension';
import { OnBlurHighlight } from './blur-extension';
import citationExtension from './citation/citation-extension';

const Title = Node.create({
  name: 'title',
  content: 'inline*',

  group: 'block',
  marks: '',
  parseHTML: () => [{ tag: 'h1:first-child' }],
  renderHTML({ HTMLAttributes }) {
    return [
      'h1',
      {
        ...HTMLAttributes,
        class: '!text-[2.5rem] !leading-[1.1] !tracking-[-0.075rem] !mt-0',
      },
      0,
    ];
  },
  addKeyboardShortcuts() {
    return {
      'Shift-Enter': ({ editor }) => {
        if (editor.state.selection.$anchor.parent.type.name === 'title') {
          return true;
        }
        return false;
      },
    };
  },
});

// Create a dedicated plugin for backspace handling
const backspaceFixPlugin = new Plugin({
  key: new PluginKey('backspaceFix'),
  props: {
    handleKeyDown(view, event) {
      if (event.key !== 'Backspace') return false;

      const { state } = view;
      const { selection } = state;
      const { empty, $head } = selection;

      // Only handle when cursor is at the start of a node
      if (!empty || $head.pos !== $head.start()) {
        return false;
      }

      // Check if current node is an empty paragraph
      const isEmpty = $head.parent.content.size === 0;
      const currentNode = $head.parent;

      // Get index and position of the current node
      const currentNodeIndex = $head.index($head.depth - 1);

      // Only proceed if we're in the second node (right after title) AND it's a paragraph (not a list item)
      if (currentNodeIndex !== 1 || currentNode.type.name !== 'paragraph') {
        return false;
      }

      // Additional check: make sure we're not inside a list
      let parentDepth = $head.depth - 2; // Go up one more level to check parent
      while (parentDepth >= 0) {
        const parentNode = $head.node(parentDepth);
        if (
          parentNode.type.name === 'bulletList' ||
          parentNode.type.name === 'orderedList' ||
          parentNode.type.name === 'taskList'
        ) {
          return false; // Let the list handle its own backspace behavior
        }
        parentDepth--;
      }

      if (isEmpty) {
        // Get the title node (first node in document)
        const titleNode = state.doc.firstChild;

        if (titleNode && titleNode.type.name === 'title') {
          // Create a transaction
          const tr = state.tr;

          // Delete current empty paragraph
          tr.delete($head.before(), $head.after());

          // Calculate position at the end of title
          const endOfTitlePos = 1 + titleNode.content.size;

          // Set selection at end of title
          tr.setSelection(TextSelection.create(tr.doc, endOfTitlePos));

          // Dispatch the transaction
          view.dispatch(tr);

          return true;
        }
      }

      return false;
    },
  },
});

const BackspaceHandler = Node.create({
  name: 'backspaceHandler',
  priority: 1000,

  addProseMirrorPlugins() {
    return [backspaceFixPlugin];
  },
});

const CustomDocument = Document.extend({
  content: 'title block+',
});

//TODO I am using cn here to get tailwind autocomplete working, idk if someone else can write a regex to just capture the class key in objects
//You can overwrite the placeholder with your own configuration
const titlePlaceholder = Placeholder.configure({
  showOnlyCurrent: false,
  emptyNodeClass: 'is-title-empty',
  placeholder: ({ node }) => {
    if (node.type.name === 'title') return 'Give it a title...';

    return 'Start writing, or type "++" for AI autocomplete';
  },
  includeChildren: false,
});

// Custom content placeholder implementation
const ContentPlaceholder = Placeholder.extend({
  addOptions() {
    return {
      ...this.parent?.(),
      emptyNodeClass: 'is-content-empty',
      showOnlyCurrent: false,
      showOnlyWhenEditable: true,
      placeholder: 'Start writing, or type "++" for AI autocomplete',
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('content-placeholder'),
        props: {
          decorations: ({ doc, selection }) => {
            const active =
              this.editor.isEditable || !this.options.showOnlyWhenEditable;
            const { anchor } = selection;
            const decorations: Decoration[] = [];

            if (!active) {
              return null;
            }

            let hasContent = false;
            doc.descendants((node, _, parent, index) => {
              if (parent === doc && index === 0) return false;

              if (node.content.size > 0) {
                hasContent = true;
                return false;
              }

              return true;
            });

            let cursorIsInEmptyNode = false;
            doc.descendants((node, pos) => {
              if (node.type.name === 'title') return false;

              const hasAnchor = anchor >= pos && anchor <= pos + node.nodeSize;
              const isEmpty = !node.isLeaf && isNodeEmpty(node);

              if (hasAnchor && isEmpty) {
                cursorIsInEmptyNode = true;
                return false;
              }

              return true;
            });

            let firstContentNodeFound = false;
            let placeholderAdded = false;

            doc.descendants((node, pos) => {
              if (node.type.name === 'title') return false;

              const hasAnchor = anchor >= pos && anchor <= pos + node.nodeSize;
              const isEmpty = !node.isLeaf && isNodeEmpty(node);

              if (!isEmpty) {
                if (!firstContentNodeFound) {
                  firstContentNodeFound = true;
                }
                return this.options.includeChildren;
              }

              // Already added a placeholder
              if (placeholderAdded) return this.options.includeChildren;

              // PRIORITY 1: If cursor is in any empty node, only show placeholder on cursor
              if (cursorIsInEmptyNode) {
                if (hasAnchor) {
                  const classes = [this.options.emptyNodeClass];
                  const decoration = Decoration.node(pos, pos + node.nodeSize, {
                    class: classes.join(' '),
                    'data-placeholder':
                      typeof this.options.placeholder === 'function'
                        ? this.options.placeholder({
                            editor: this.editor,
                            node,
                            pos,
                            hasAnchor,
                          })
                        : this.options.placeholder,
                  });
                  decorations.push(decoration);
                  placeholderAdded = true;
                }
              }
              // PRIORITY 2: If no cursor in any empty node, show on first empty node
              else if (!firstContentNodeFound && !hasContent) {
                firstContentNodeFound = true;
                const classes = [this.options.emptyNodeClass];
                const decoration = Decoration.node(pos, pos + node.nodeSize, {
                  class: classes.join(' '),
                  'data-placeholder':
                    typeof this.options.placeholder === 'function'
                      ? this.options.placeholder({
                          editor: this.editor,
                          node,
                          pos,
                          hasAnchor,
                        })
                      : this.options.placeholder,
                });
                decorations.push(decoration);
                placeholderAdded = true;
              } else if (!firstContentNodeFound) {
                firstContentNodeFound = true;
              }

              return this.options.includeChildren;
            });

            return DecorationSet.create(doc, decorations);
          },
        },
      }),
    ];
  },
});

const contentPlaceholder = ContentPlaceholder.configure();

const tiptapLink = TiptapLink.configure({
  HTMLAttributes: {
    class: cn(
      'underline underline-offset-[3px] transition-all duration-100 ease-in cursor-pointer !font-normal !text-foreground hover:!text-muted-foreground',
    ),
  },
});

const tiptapImage = TiptapImage.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      fullWidth: {
        default: false,
      },
      allowBase64: {
        default: true,
      },
    };
  },
  renderHTML({ HTMLAttributes }) {
    const classes = HTMLAttributes.fullWidth
      ? 'my-4 w-[80vw]'
      : 'my-4 w-full border border-border';
    return ['img', { ...HTMLAttributes, class: classes }];
  },
  addProseMirrorPlugins() {
    return [UploadImagesPlugin({ imageClass: '' })];
  },
});

const taskList = TaskList.configure({
  HTMLAttributes: {
    class: cn('not-prose pl-4 my-4'),
  },
});
const taskItem = TaskItem.configure({
  HTMLAttributes: {
    class: cn('flex gap-1 items-start'),
  },
  nested: true,
});

const MarkdownExtension = Markdown.configure({
  transformPastedText: false,
});

export const starterKit = (isTeam: boolean) =>
  StarterKit.configure({
    ...(isTeam && { history: false }),
    document: false,
    bulletList: {
      HTMLAttributes: {
        class: 'list-disc not-prose pl-9  my-4 text-foreground',
      },
    },
    orderedList: {
      HTMLAttributes: {
        class:
          'list-decimal not-prose pl-9 my-4  marker:font-sans marker:normal-nums	text-foreground',
      },
    },
    listItem: {
      HTMLAttributes: {
        class:
          'my-1 marker:text-secondary-foreground [&>ul]:my-0 [&>ol]:my-0 text-foreground',
      },
    },
    blockquote: {
      HTMLAttributes: {
        class: 'border-l-4 not-prose border-border text-foreground pl-3 my-2',
      },
    },
    codeBlock: {
      HTMLAttributes: {
        class:
          'block border-none not-prose m-auto my-1 p-4 bg-muted text-muted-foreground font-medium text-sm',
      },
    },
    code: {
      HTMLAttributes: {
        class:
          'rounded-sm bg-muted not-prose px-1 py-0.5 font-mono font-semibold text-muted-foreground text-sm',
        spellcheck: 'false',
      },
    },
    horizontalRule: {
      HTMLAttributes: {
        class: 'border-none h-px my-2 bg-border text-foreground',
      },
    },
  });

// const youtube = Youtube.configure({
//   HTMLAttributes: {
//     class: cn('rounded-lg border border-muted'),
//   },
//   inline: false,
// });

// const twitter = Twitter.configure({
//   HTMLAttributes: {
//     class: cn('not-prose'),
//   },
//   inline: false,
// });

// const mathematics = Mathematics.configure({
//   HTMLAttributes: {
//     class: cn('text-foreground rounded p-1 hover:bg-accent cursor-pointer'),
//   },
//   katexOptions: {
//     throwOnError: false,
//   },
// });

// const characterCount = CharacterCount.configure();

export const defaultExtensions = [
  Title,
  CustomDocument,
  titlePlaceholder,
  contentPlaceholder,
  tiptapLink,
  tiptapImage,
  taskList,
  MarkdownExtension,
  taskItem,
  // horizontalRule,
  // codeBlockLowlight,
  TiptapUnderline,
  HighlightExtension.extend({
    addKeyboardShortcuts() {
      return {
        'Mod-Shift-h': () =>
          this.editor.commands.toggleHighlight({ color: '#FFFBE6' }),
      };
    },
  }).configure({
    multicolor: true,
  }),
  TextStyle,
  CustomKeymap.extend({
    addKeyboardShortcuts() {
      return {
        'Shift-Enter': () => {
          return false;
        },
      };
    },
  }),
  CustomSearch.configure({
    searchResultClass: 'search-result',
    caseSensitive: false,
    disableRegex: false,
  }),
  citationExtension,
  BackspaceHandler,
  OnBlurHighlight,
  // Table extensions for proper table support
  Table.configure({
    resizable: true,
  }),
  TableRow,
  TableHeader,
  TableCell,
];
